import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Tooltip,
  useTheme as useMuiTheme,
  useMediaQuery,
  SvgIcon
} from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearSearch, showAllProducts, showFavorites, showCategories, showBrands, showPromotions } from "../../store/slices/productsSlice";
import { openUserManager } from "../../store/slices/userManagerSlice";
import { openRoleManager } from "../../store/slices/roleManagerSlice";
import { openPromotionManager } from "../../store/slices/promotionSlice";
import { openCategoryManager } from "../../store/slices/categoryManagerSlice";
import { openBrandManager } from "../../store/slices/brandManagerSlice";
import { openHiperSync } from "../../store/slices/hiperSyncSlice";
import { openProductManager } from "../../store/slices/productManagerSlice";
import { openLandpageManager, showLandpageView, hideLandpageView } from "../../store/slices/landpageManagerSlice";
import Menu from "../Menu/Menu";
import { menuData, handle } from "../Menu/MenuOptions";
import { useTheme } from "../../contexts/AccessibilityContext";
import Logo from "./Logo/Logo";
import SearchBar from "./SearchBar/SearchBar";
import UserActions from "./UserActions/UserActions";
import CartDrawer from "./CartDrawer/CartDrawer";

/**
 * Componente de cabeçalho responsivo fixo no topo da página.
 * Layout adaptativo: mobile (2 linhas) e desktop (2 linhas com distribuição diferente).
 * Integra Logo, SearchBar, UserActions, Menu e CartDrawer.
 *
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {string} [props.logoSrc="/vika_icon.png"] - Caminho do logo
 * @param {Function} [props.onClickUser] - Callback para clique no avatar (deprecated)
 * @param {Function} [props.onClickCart] - Callback para clique no carrinho (deprecated)
 * @returns {JSX.Element} Header responsivo com navegação completa
 */
function Header({
  //logoSrc = "/vika_icon.png",
  //logoSrc = "/imagens/icons/svg/vikanaweb-logo-text-center-black.svg",
  logoSrc = "/vika_icon.png",
  onClickUser = () => {
    /* TODO: ação futura */
  },
  onClickCart = () => {
    /* TODO: ação futura */
  }
}) {
  const { changeTheme } = useTheme();
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cartOpen, setCartOpen] = useState(false);
  const iconSize = isMobile ? 30 : 36;

  /**
   * Observer callback para ações do menu
   * @param {string} eventType - EventType do item clicado
   */
  const onMenuAction = useCallback(
    (eventType) => {
      const ctx = {
        navigate,
        setAccessibility: ({ mode }) => changeTheme(mode),
        notify: (message) => console.log(message),
        clearSearch: () => dispatch(clearSearch()),
        showFavorites: () => dispatch(showFavorites()),
        showAllProducts: () => {
          dispatch(showAllProducts());
          dispatch(showLandpageView());
        },
        showAllProductsGrid: () => {
          dispatch(showAllProducts());
          dispatch(hideLandpageView());
        },
        showCategories: () => dispatch(showCategories()),
        showBrands: () => dispatch(showBrands()),
        showPromotions: () => dispatch(showPromotions()),
        openUserManager: () => dispatch(openUserManager()),
        openRoleManager: () => dispatch(openRoleManager()),
        openPromotionManager: () => dispatch(openPromotionManager()),
        openCategoryManager: () => dispatch(openCategoryManager()),
        openBrandManager: () => dispatch(openBrandManager()),
        openHiperSync: () => dispatch(openHiperSync()),
        openProductManager: () => dispatch(openProductManager()),
        openLandpageManager: () => dispatch(openLandpageManager()),
      };
      handle(eventType, ctx);
    },
    [navigate, changeTheme, dispatch]
  );

  return (
    <AppBar
      position="fixed"
      color="transparent"
      sx={{
        zIndex: theme.zIndex.appBar,
        flexDirection: "column",
        backgroundColor: 'rgba(107, 56, 54, 0.23)',
        backdropFilter: 'blur(2px)',
        color: '#FFFFFF',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.10)',
      }}
    >
      {isMobile ? (
        <>
          {/* Primeira linha mobile: Logo à esquerda, ícones à direita */}
          <Toolbar sx={{ minHeight: 64, px: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'transparent' }}>
            <Logo src={logoSrc} isMobile={true} />
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <IconButton
                color="inherit"
                size="small"
                onClick={() => {
                  dispatch(showAllProducts());
                  dispatch(clearSearch());
                  dispatch(showLandpageView());
                }}
              >
                <SvgIcon sx={{ fontSize: iconSize, color: '#B5745D' }}>
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M3 11.5L12 4l9 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5.5 10.5V20h13V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 20v-6h4v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </SvgIcon>
              </IconButton>
              <UserActions
                onCartClick={() => setCartOpen(true)}
                onUserClick={onClickUser}
                isMobile={true}
              />
            </Box>
          </Toolbar>

          {/* Segunda linha mobile: Menu + Pesquisa */}
          <Toolbar sx={{ minHeight: 56, px: 0, pr: 1 }}>
            <Tooltip title="Início">
              <Box sx={{ display: "flex", alignItems: "center", mr: 0, mt: 0 }}>
                <Menu data={menuData} onAction={onMenuAction} />
              </Box>
            </Tooltip>
            <SearchBar fullWidth={true} />
          </Toolbar>
        </>
      ) : (
        <>
          {/* Layout Desktop - Primeira linha */}
          <Toolbar sx={{ minHeight: 100, mx: 0, px: 0, bgcolor: 'transparent' }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                paddingTop: "15px",
              }}
            >
              <Logo src={logoSrc} isMobile={false} />
              <UserActions
                onCartClick={() => setCartOpen(true)}
                onUserClick={onClickUser}
                isMobile={false}
                showFavorites={false}
              />
            </Box>
          </Toolbar>

          {/* Layout Desktop - Segunda linha */}
          <Toolbar sx={{ minHeight: 64, px: 0.5 , pr: 1  }}>
            <Box
              sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <Menu data={menuData} onAction={onMenuAction} />
              </Box>
              <Box sx={{ width: "100%", mb: 1 }}>
                <SearchBar fullWidth={true} />
              </Box>
            </Box>
          </Toolbar>
        </>
      )}

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </AppBar>
  );
}

export default Header;
